import { AvailabilityRule } from '@/types/revenue-manager';
import { delay, API_ENDPOINTS, apiClient, ApiResponse, USE_MOCK } from './client';
import { mockAvailabilityRulesData } from '@/data/mock-data';
import { transformAvailabilityRuleFromAPI, ApiAvailabilityRule, transformAvailabilityRuleForAPI, RawApiAvailabilityRule } from '@/utils/availability-rule-transform';

export interface AvailabilityRulesResponse {
  success: boolean;
  data: AvailabilityRule[];
  total: number;
  propertyId: string;
}

// ============ API Functions ============

export async function fetchAvailabilityRules(propertyId: string): Promise<AvailabilityRulesResponse> {
  if (USE_MOCK) {
    await delay();
    const rules = mockAvailabilityRulesData[propertyId] || [];
    return {
      success: true,
      data: rules,
      total: rules.length,
      propertyId,
    };
  }
  
  // Use generic API endpoint with filters parameter
  // Both availability and pricing rules are stored in PricingRule table, differentiated by rule_type
  const filters = JSON.stringify([{
    resource_name: "PricingRule",
    columns: ["*", "property_id", "is_active", "rule_type"],
    column_filters: [{
      property_id: propertyId,
      // is_active: true,
      rule_type: "availability"
    }]
  }]);
  
  const response = await apiClient.get<any>(
    `${API_ENDPOINTS.generic}?filters=${encodeURIComponent(filters)}`
  );
  
  // Extract data from the nested response structure
  const apiRulesData = response.data?.data?.[0]?.PricingRule_data || [];
  // console.log('Full API Response (Availability):', JSON.stringify(response.data, null, 2));
  // console.log('API availability rules data:', apiRulesData);
  // console.log('API availability rules data length:', apiRulesData.length);
  
  // if (apiRulesData.length > 0) {
  //   console.log('First availability rule structure:', JSON.stringify(apiRulesData[0], null, 2));
  // }
  
  // Transform API format to internal format
  const transformedRules = apiRulesData.map((apiRule: unknown, index: number) => {
    // console.log(`Transforming availability rule ${index}:`, apiRule);
    try {
      const transformed = transformAvailabilityRuleFromAPI(apiRule as RawApiAvailabilityRule);
      // console.log(`Transformed availability rule ${index}:`, transformed);
      return transformed;
    } catch (error) {
      console.error(`Error transforming availability rule ${index}:`, error);
      throw error;
    }
  });
  // console.log('Transformed availability rules:', transformedRules);
  
  return {
    success: response.data.success,
    data: transformedRules,
    total: response.data?.data?.[0]?.total || 0,
    propertyId,
  };
}

export async function createAvailabilityRule(
  propertyId: string,
  rule: any
): Promise<ApiResponse<AvailabilityRule>> {
  if (USE_MOCK) {
    await delay();
    const now = new Date().toISOString();
    const newRule: AvailabilityRule = {
      ...rule,
      id: `avail-${Date.now()}`,
      createdAt: now,
      updatedAt: now,
    };
    
    if (!mockAvailabilityRulesData[propertyId]) {
      mockAvailabilityRulesData[propertyId] = [];
    }
    mockAvailabilityRulesData[propertyId].push(newRule);
    
    return {
      success: true,
      data: newRule,
    };
  }
  
  // Use unified revenue rules endpoint
  const response = await apiClient.post<ApiResponse<AvailabilityRule>>(
    API_ENDPOINTS.revenueRules.create,
    rule
  );
  return response.data;
}

export async function updateAvailabilityRule(
  propertyId: string,
  ruleId: string,
  updates: any
): Promise<ApiResponse<AvailabilityRule | null>> {
  if (USE_MOCK) {
    await delay();
    const rules = mockAvailabilityRulesData[propertyId] || [];
    const index = rules.findIndex(r => r.id === ruleId);
    
    if (index === -1) {
      return { success: false, data: null };
    }
    
    rules[index] = { ...rules[index], ...updates, updatedAt: new Date().toISOString() };
    return {
      success: true,
      data: rules[index],
    };
  }
  
  // Use unified revenue rules endpoint with POST method
  // Add id to the payload (same as create but with id)
  const payload = {
    ...updates,
    id: ruleId
  };
  
  const response = await apiClient.post<ApiResponse<AvailabilityRule | null>>(
    API_ENDPOINTS.revenueRules.create,
    payload
  );
  return response.data;
}

export async function deleteAvailabilityRule(
  propertyId: string,
  ruleId: string
): Promise<ApiResponse<null>> {
  if (USE_MOCK) {
    await delay();
    const rules = mockAvailabilityRulesData[propertyId] || [];
    const index = rules.findIndex(r => r.id === ruleId);
    
    if (index !== -1) {
      rules.splice(index, 1);
  }
    return { success: true, data: null };
  }
  
  // Use generic API endpoint with DELETE method
  const payload = [{
    resource_name: "PricingRule",
    column_value_pair: {
      ids: [ruleId]
    }
  }];
  
  const response = await apiClient.delete<ApiResponse<null>>(
    API_ENDPOINTS.generic,
    { data: payload }
  );
  return response.data;
}

export async function toggleAvailabilityRule(
  propertyId: string,
  ruleId: string,
  isActive: boolean
): Promise<ApiResponse<AvailabilityRule | null>> {
  if (USE_MOCK) {
    await delay(200);
    const rules = mockAvailabilityRulesData[propertyId] || [];
    const rule = rules.find(r => r.id === ruleId);
    
    if (!rule) {
      return { success: false, data: null };
    }
    
    rule.isActive = !rule.isActive;
    rule.updatedAt = new Date().toISOString();
    
    return {
      success: true,
      data: rule,
    };
  }
  
  const payload = [
    {
      resource_name: "PricingRule",
      column_value_pair: [
        {
          id: parseInt(ruleId),
          is_active: !isActive
        }
      ]
    }
  ];
  
  const response = await apiClient.patch<ApiResponse<AvailabilityRule | null>>(
    API_ENDPOINTS.generic,
    payload
  );
  return response.data;
}

export async function runAvailabilityRule(
  propertyId: string,
  ruleId: string
): Promise<ApiResponse<{ executed: boolean; affectedRooms: number }>> {
  if (USE_MOCK) {
    await delay(500);
    const rules = mockAvailabilityRulesData[propertyId] || [];
    const rule = rules.find(r => r.id === ruleId);
    
    if (rule) {
      rule.lastRun = new Date().toISOString();
    }
    
    return {
      success: true,
      data: { executed: true, affectedRooms: Math.floor(Math.random() * 30) + 5 },
    };
  }
  
  const response = await apiClient.post<ApiResponse<{ executed: boolean; affectedRooms: number }>>(
    API_ENDPOINTS.availabilityRules.run(propertyId, ruleId),
    {}
  );
  return response.data;
}

export async function importAvailabilityRules(
  targetPropertyIds: string[],
  rules: Omit<AvailabilityRule, 'id' | 'createdAt' | 'updatedAt'>[]
): Promise<ApiResponse<{ importedCount: number }>> {
  if (USE_MOCK) {
    await delay(500);
    const now = new Date().toISOString();
    let importedCount = 0;
    
    for (const propertyId of targetPropertyIds) {
      if (!mockAvailabilityRulesData[propertyId]) {
        mockAvailabilityRulesData[propertyId] = [];
      }
      
      for (const rule of rules) {
        const newRule: AvailabilityRule = {
          ...rule,
          id: `avail-${Date.now()}-${importedCount}`,
          createdAt: now,
          updatedAt: now,
        };
        mockAvailabilityRulesData[propertyId].push(newRule);
        importedCount++;
      }
    }
    
    return {
      success: true,
      data: { importedCount },
    };
  }
  
  const response = await apiClient.post<ApiResponse<{ importedCount: number }>>(
    API_ENDPOINTS.revenueRules.import,
    { targetPropertyIds, rules }
  );
  return response.data;
}

export async function exportAvailabilityRules(
  propertyId: string
): Promise<ApiResponse<AvailabilityRule[]>> {
  if (USE_MOCK) {
    await delay();
    return {
      success: true,
      data: mockAvailabilityRulesData[propertyId] || [],
    };
  }
  
  const response = await apiClient.get<ApiResponse<AvailabilityRule[]>>(
    API_ENDPOINTS.availabilityRules.export(propertyId)
  );
  return response.data;
}
